import React, { useState, useRef, useEffect } from 'react';
import { Event } from '../../types';
import { CalendarIcon, PlusIcon } from "@phosphor-icons/react";
import './events.css';

interface EventSelectorProps {
  events: Event[];
  selectedEventId?: number;
  onEventSelect: (eventId: number) => void;
  showCreateButton?: boolean;
  onCreateEvent?: () => void;
  isLoading?: boolean;
}

const EventSelector: React.FC<EventSelectorProps> = ({
  events,
  selectedEventId,
  onEventSelect,
  showCreateButton = false,
  onCreateEvent,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedEvent = events.find(event => event.id === selectedEventId);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleEventSelect = (eventId: number) => {
    onEventSelect(eventId);
    setIsOpen(false);
  };



  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Filter only active (open) events that are not past and sort by date
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for comparison
  
  const activeEvents = events.filter(event => {
    const eventDate = new Date(event.eventDate);
    eventDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
    return event.status === 'open' && eventDate >= today;
  });
  
  const sortedEvents = [...activeEvents].sort((a, b) => {
    return new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
  });

  if (isLoading) {
    return (
      <div className="event-selector loading">
        <div className="selector-button disabled">
          <div className="loading-spinner small"></div>
          <span>Caricamento eventi...</span>
        </div>
      </div>
    );
  }

  if (activeEvents.length === 0) {
    return (
      <div className="event-selector empty">
        <div className="event-selector-empty-state">
          <div className="event-selector-empty-message">
            <CalendarIcon
              size={24}
              weight="light"
              className="event-selector-empty-icon"
            />
            <span>Nessun evento</span>
          </div>
          {showCreateButton && onCreateEvent && (
            <button className="create-first-event-button" onClick={onCreateEvent}>
              Crea il primo evento
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="event-selector" ref={dropdownRef}>
      <button
        className={`selector-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {selectedEvent ? (
          <div className="selected-event">
            <div className="event-info">
              <span className="event-name">{selectedEvent.name}</span>
              <span className="event-date">{formatDate(selectedEvent.eventDate)}</span>
            </div>
          </div>
        ) : (
          <div className="placeholder">
            <CalendarIcon size={16} weight="regular" className="placeholder-icon" />
            <span>Seleziona evento</span>
          </div>
        )}
        <div className={`dropdown-arrow ${isOpen ? 'up' : 'down'}`}>▼</div>
      </button>

      {isOpen && (
        <div className="dropdown-menu" role="listbox">
          <div className="dropdown-header">
            <span>Eventi Disponibili</span>
          </div>
          
          <div className="events-list">
            {sortedEvents.map(event => (
              <button
                key={event.id}
                className={`event-option ${selectedEventId === event.id ? 'selected' : ''}`}
                onClick={() => handleEventSelect(event.id)}
                role="option"
                aria-selected={selectedEventId === event.id}
              >
                <div className="event-option-content">
                  <div className="event-option-info">
                    <span className="event-option-name">{event.name}</span>
                    <span className="event-option-date">{formatDate(event.eventDate)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {showCreateButton && onCreateEvent && (
            <div className="dropdown-footer">
              <button className="create-event-option" onClick={onCreateEvent}>
                <PlusIcon size={16} weight="bold" className="create-icon" />
                <span>Crea nuovo evento</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventSelector;
